import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { format, subDays } from "date-fns";
import { Menu, MenuItem } from "@material-ui/core";
import "../../components/GxonTemplate/GxonTemplate.css";

const DashboardGxonTemplate = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({
        kpi: {
            total: 0,
            closed: 0,
            open: 0,
            pending: 0,
            avgWaitTime: "00h:00m",
            avgServiceTime: "00h:00m",
            newContacts: 0,
            activeAgents: 0
        },
        charts: {
            ticketsByQueue: [],
            ticketsByAgent: [],
            ticketsByChannel: []
        }
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const filters = {
                dateFrom: format(subDays(new Date(), 7), "yyyy-MM-dd"),
                dateTo: format(new Date(), "yyyy-MM-dd"),
                queueIds: "",
                userIds: "",
                connectionId: ""
            };

            const { data: result } = await api.get("/dashboard/performance", {
                params: filters
            });
            setData((prevState) => ({ ...prevState, ...result }));
        } catch (err) {
            console.error("Error fetching dashboard data:", err);
            toastError(err);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Chart Options - Bar Chart (Tickets by Queue)
    const barChartOptions = {
        chart: {
            type: 'bar',
            height: 300,
            toolbar: {
                show: false
            }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 4
            }
        },
        dataLabels: {
            enabled: false
        },
        colors: ['#7d12ce'],
        xaxis: {
            categories: data.charts.ticketsByQueue.map(item => item.name || 'Sem Fila'),
        },
        yaxis: {
            title: {
                text: 'Quantidade'
            }
        },
        fill: {
            opacity: 1
        },
        tooltip: {
            y: {
                formatter: function (val) {
                    return val + " tickets"
                }
            }
        }
    };

    const barChartSeries = [{
        name: 'Tickets',
        data: data.charts.ticketsByQueue.map(item => item.value || 0)
    }];

    // Donut Chart (Tickets by Channel)
    const donutChartOptions = {
        chart: {
            type: 'donut',
            height: 250
        },
        labels: data.charts.ticketsByChannel.map(item => item.name || 'Desconhecido'),
        colors: ['#7d12ce', '#02B4FA', '#FDBB1F', '#22B07E', '#FF401C'],
        legend: {
            show: false
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return Math.round(val) + "%"
            }
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%'
                }
            }
        }
    };

    const donutChartSeries = data.charts.ticketsByChannel.map(item => item.value || 0);

    return (
        <div className="container">
            {/* Page Header */}
            <div className="app-page-head d-flex flex-wrap gap-3 align-items-center justify-content-between">
                <div className="clearfix">
                    <h1 className="app-page-title">Dashboard</h1>
                    <span>{format(new Date(), "EEE, MMM dd, yyyy")}</span>
                </div>
                <button type="button" className="btn btn-primary waves-effect waves-light" onClick={fetchData}>
                    <i className="fi fi-rr-refresh me-1"></i> Atualizar
                </button>
            </div>

            {/* Metric Cards Row */}
            <div className="row">
                <div className="col-xxl-9">
                    <div className="row">
                        {/* Total Tickets */}
                        <div className="col-6 col-md-4 col-lg">
                            <div className="card bg-secondary bg-opacity-05 shadow-none border-0">
                                <div className="card-body">
                                    <div className="avatar bg-secondary shadow-secondary rounded-circle text-white mb-3">
                                        <i className="fi fi-sr-users"></i>
                                    </div>
                                    <h3>{data.kpi.total}</h3>
                                    <h6 className="mb-0">Total de Atendimentos</h6>
                                    <small className="fw-medium">
                                        <span className="text-success">
                                            <i className="fi fi-rr-arrow-small-up scale-3x"></i> +5%
                                        </span> Último Mês
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Closed Tickets */}
                        <div className="col-6 col-md-4 col-lg">
                            <div className="card bg-info bg-opacity-05 shadow-none border-0">
                                <div className="card-body">
                                    <div className="avatar bg-info shadow-info rounded-circle text-white mb-3">
                                        <i className="fi fi-sr-check-circle"></i>
                                    </div>
                                    <h3>{data.kpi.closed}</h3>
                                    <h6 className="mb-0">Fechados</h6>
                                    <small className="fw-medium">
                                        <span className="text-success">
                                            <i className="fi fi-rr-arrow-small-up scale-3x"></i> +3.2%
                                        </span> Último Mês
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Open Tickets */}
                        <div className="col-6 col-md-4 col-lg">
                            <div className="card bg-warning bg-opacity-05 shadow-none border-0">
                                <div className="card-body">
                                    <div className="avatar bg-warning shadow-warning rounded-circle text-white mb-3">
                                        <i className="fi fi-sr-exclamation"></i>
                                    </div>
                                    <h3>{data.kpi.open}</h3>
                                    <h6 className="mb-0">Abertos</h6>
                                    <small className="fw-medium">
                                        <span className="text-danger">
                                            <i className="fi fi-rr-arrow-small-down scale-3x"></i> -2%
                                        </span> Último Mês
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Pending Tickets */}
                        <div className="col-6 col-md-6 col-lg">
                            <div className="card bg-success bg-opacity-05 shadow-none border-0">
                                <div className="card-body">
                                    <div className="avatar bg-success shadow-success rounded-circle text-white mb-3">
                                        <i className="fi fi-sr-time-forward"></i>
                                    </div>
                                    <h3>{data.kpi.pending}</h3>
                                    <h6 className="mb-0">Pendentes</h6>
                                    <small className="fw-medium">
                                        <span className="text-success">
                                            <i className="fi fi-rr-arrow-small-up scale-3x"></i> +8%
                                        </span> Último Mês
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Avg Wait Time */}
                        <div className="col-6 col-md-4 col-lg">
                            <div className="card bg-danger bg-opacity-05 shadow-none border-0">
                                <div className="card-body">
                                    <div className="avatar bg-danger shadow-danger rounded-circle text-white mb-3">
                                        <i className="fi fi-sr-clock-three"></i>
                                    </div>
                                    <h3>{data.kpi.avgWaitTime}</h3>
                                    <h6 className="mb-0">T.M. Espera</h6>
                                    <small className="fw-medium">
                                        <span className="text-danger">
                                            <i className="fi fi-rr-arrow-small-down scale-3x"></i> -8%
                                        </span> Último Mês
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create Announcement Card */}
                <div className="col-xxl-3">
                    <div className="card overflow-hidden z-1">
                        <div className="card-body">
                            <div className="w-75">
                                <h6 className="card-title">Novo Atendimento</h6>
                                <p>Iniciar uma nova conversa com um contato</p>
                            </div>
                        </div>
                        <div className="card-footer border-0 pt-0">
                            <a href="/tickets" className="btn btn-outline-light waves-effect btn-shadow">Iniciar Agora</a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Section Row */}
            <div className="row">
                <div className="col-xxl-4 col-lg-4">

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between border-0 pb-0">
                            <h6 className="card-title mb-0">Performance dos Atendentes</h6>
                        </div>
                        <div className="card-body">
                            <div className="row align-items-center">
                                <div className="col-sm-6">
                                    <div className="maxw-250px ratio ratio-1x1">
                                        {data.charts.ticketsByAgent.length > 0 ? (
                                            <div style={{ position: 'relative' }}>
                                                <Chart
                                                    options={{
                                                        chart: { type: 'donut', height: 250 },
                                                        labels: data.charts.ticketsByAgent.slice(0, 5).map(item => item.name || 'Sem nome'),
                                                        colors: ['#7d12ce', '#02B4FA', '#FDBB1F', '#22B07E', '#FF401C'],
                                                        legend: { show: false },
                                                        dataLabels: { enabled: false },
                                                        plotOptions: {
                                                            pie: {
                                                                donut: {
                                                                    size: '70%',
                                                                    labels: {
                                                                        show: true,
                                                                        name: { show: false },
                                                                        value: { show: false },
                                                                        total: {
                                                                            show: true,
                                                                            showAlways: true,
                                                                            label: 'Total',
                                                                            fontSize: '14px',
                                                                            color: '#999',
                                                                            formatter: function (w) {
                                                                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                                                                            }
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    series={data.charts.ticketsByAgent.slice(0, 5).map(item => item.value || 0)}
                                                    type="donut"
                                                    height={250}
                                                />
                                            </div>
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center" style={{ height: '250px' }}>
                                                <p className="text-muted">Sem dados</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="d-grid gap-2">
                                        {data.charts.ticketsByAgent.slice(0, 5).map((agent, index) => {
                                            const colors = ['#7d12ce', '#02B4FA', '#FDBB1F', '#22B07E', '#FF401C'];
                                            const total = data.charts.ticketsByAgent.slice(0, 5).reduce((sum, a) => sum + (a.value || 0), 0);
                                            const percentage = total > 0 ? Math.round((agent.value / total) * 100) : 0;
                                            return (
                                                <div key={index} className="d-flex gap-2 align-items-center">
                                                    <i className="fa fa-circle text-2xs" style={{ color: colors[index] }}></i>
                                                    <strong className="text-dark fw-semibold">{percentage}%</strong>
                                                    <span className="text-muted">{agent.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xxl-4 col-lg-4">

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between border-0 pb-0">
                            <h6 className="card-title mb-0">Tickets por Fila</h6>
                        </div>
                        <div className="card-body p-2">
                            {data.charts.ticketsByQueue.length > 0 ? (
                                <Chart
                                    options={barChartOptions}
                                    series={barChartSeries}
                                    type="bar"
                                    height={300}
                                />
                            ) : (
                                <div style={{ minHeight: '300px' }} className="d-flex align-items-center justify-content-center">
                                    <p className="text-muted">Sem dados disponíveis</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-xxl-4 col-lg-4">

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between border-0 pb-0">
                            <h6 className="card-title mb-0">Tickets por Canal</h6>
                        </div>
                        <div className="card-body">
                            <div className="row align-items-center">
                                <div className="col-sm-6">
                                    {data.charts.ticketsByChannel.length > 0 ? (
                                        <Chart
                                            options={donutChartOptions}
                                            series={donutChartSeries}
                                            type="donut"
                                            height={250}
                                        />
                                    ) : (
                                        <div className="d-flex align-items-center justify-content-center" style={{ height: '250px' }}>
                                            <p className="text-muted">Sem dados</p>
                                        </div>
                                    )}
                                </div>
                                <div className="col-sm-6">
                                    <div className="d-grid gap-2">
                                        {data.charts.ticketsByChannel.map((channel, index) => (
                                            <div key={index} className="d-flex gap-1 align-items-center mx-1">
                                                <i className="fa fa-circle text-primary text-2xs me-1"></i>
                                                <strong className="text-dark fw-semibold">{channel.value}</strong> {channel.name}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardGxonTemplate;
